import type { Component } from 'vue'
/**
 * 用户中心 v2 导航配置（单一数据源）
 * 目标：集中管理用户中心的侧边栏 + 头像下拉菜单导航结构，支持基于 route.name 的跳转
 *
 * 设计说明：
 * - 顶层为 items 数组，包含 5 个主分组：用户中心、身份升级、模型业务、任务业务、财务管理
 * - 每个导航项包含字段：id, title, routeName?, iconComponent?, children?
 * - id 统一使用路由 path 的末段（kebab-case），与 sidebarNavigationConfig 的 key 保持一致
 *
 * ❗ 改动须知：此文件为导航唯一数据源
 *   - 头像下拉菜单（HeaderTopBar.vue）通过 HEADER_DROPDOWN_ITEMS 引用
 *   - 侧边栏（navigation store）通过 userCenterNavConfig 引用
 *   - 修改 id / routeName 时必须同步所有引用点（包括 useTaskConsole.ts 等 updateActiveId 调用）
 */
import type { NavConfig } from '@/config/navigation/types'

import {
  Box,
  ChatLineSquare,
  Checked,
  Document,
  EditPen,
  List,
  Money,
  OfficeBuilding,
  Setting,
  Star,
  SwitchButton,
  Upload,
  User,
  Wallet,
} from '@element-plus/icons-vue'

export const userCenterNavConfig: NavConfig = {
  items: [
    // 1) 账户管理
    {
      id: 'user-center',
      title: '账户管理',
      children: [
        { id: 'dashboard', title: '工作台', routeName: 'UserCenterHome', iconComponent: User },
        { id: 'messages', title: '消息中心', routeName: 'UserMessageCenter', iconComponent: ChatLineSquare },
        { id: 'settings', title: '账号设置', routeName: 'UserSettings', iconComponent: Setting },
        { id: 'project-experience', title: '项目经验', routeName: 'ProjectExperience', iconComponent: EditPen },
        { id: 'qualifications', title: '资质证书', routeName: 'UserQualifications', iconComponent: Document },
      ],
    },

    // 2) 身份升级
    {
      id: 'identity-upgrade',
      title: '身份升级',
      children: [
        { id: 'designer-registration', title: '设计师注册', routeName: 'DesignerRegistration', iconComponent: EditPen },
        { id: 'provider-registration', title: '服务商注册', routeName: 'ProviderRegistration', iconComponent: OfficeBuilding },
      ],
    },

    // 3) 模型业务管理
    {
      id: 'model',
      title: '模型业务管理',
      children: [
        { id: 'model-upload', title: '模型上传', routeName: 'ModelUpload', iconComponent: Upload },
        { id: 'my-models', title: '我的模型', routeName: 'MyModels', iconComponent: Box },
        { id: 'model-collection', title: '模型收藏', routeName: 'ModelCollection', iconComponent: Star },
      ],
    },

    // 4) 任务业务管理
    {
      id: 'task',
      title: '任务业务管理',
      children: [
        { id: 'task-publish', title: '发布任务', routeName: 'TaskPublish', iconComponent: EditPen },
        { id: 'published-tasks', title: '发布管理', routeName: 'PublishedTasks', iconComponent: List },
        { id: 'accepted-tasks', title: '承接管理', routeName: 'AcceptedTasks', iconComponent: Checked },
        // v4.6极简方案: 删除任务工作台导航，控制台通过列表页进入
      ],
    },

    // 5) 财务管理
    {
      id: 'finance',
      title: '财务管理',
      children: [
        { id: 'account-overview', title: '财务总览', routeName: 'AccountOverview', iconComponent: Wallet },
        { id: 'transaction-records', title: '交易记录', routeName: 'TransactionRecords', iconComponent: Document },
        { id: 'recharge', title: '在线充值', routeName: 'Recharge', iconComponent: Money },
        { id: 'vip-purchase', title: 'VIP会员', routeName: 'VipPurchase', iconComponent: Star },
        { id: 'payment-withdraw', title: '提现管理', routeName: 'PaymentWithdraw', iconComponent: Money },
        { id: 'deposit', title: '诚信保证金', routeName: 'Deposit', iconComponent: Money },
      ],
    },
  ],
}

/**
 * 头像下拉菜单导航项
 * @description 从 userCenterNavConfig 中提取，保证与侧边栏使用同一数据源
 * 顺序：个人中心 → 消息中心 → 账号设置 | 我的模型 → 模型收藏 → 承接管理 → 发布管理 | 财务中心 → 退出
 */
export interface HeaderDropdownItem {
  key: string
  label: string
  routeName: string
  iconComponent: Component
  type?: 'divider'
}

/** 从配置中按 id 查找导航项 */
function findNavItem(id: string) {
  for (const group of userCenterNavConfig.items) {
    if (group.children) {
      const item = group.children.find(child => child.id === id)
      if (item)
        return item
    }
  }
  return undefined
}

/** 构建下拉菜单项（从统一配置提取，杜绝硬编码路径） */
function buildDropdownItem(navId: string): HeaderDropdownItem | undefined {
  const nav = findNavItem(navId)
  if (!nav || !nav.routeName || !nav.iconComponent)
    return undefined
  return {
    key: nav.id,
    label: nav.title,
    routeName: nav.routeName,
    iconComponent: nav.iconComponent,
  }
}

/**
 * 头像下拉菜单项列表（有序）
 * 修改此列表即可调整下拉菜单的显示内容和顺序
 */
export const HEADER_DROPDOWN_ITEMS: (HeaderDropdownItem | { key: string, type: 'divider' } | { key: string, type: 'logout', label: string, iconComponent: Component })[] = [
  buildDropdownItem('dashboard')!,
  buildDropdownItem('messages')!,
  buildDropdownItem('settings')!,
  { key: 'divider-1', type: 'divider' as const },
  buildDropdownItem('my-models')!,
  buildDropdownItem('model-collection')!,
  buildDropdownItem('accepted-tasks')!,
  buildDropdownItem('published-tasks')!,
  { key: 'divider-2', type: 'divider' as const },
  buildDropdownItem('account-overview')!,
  { key: 'logout', type: 'logout' as const, label: '退出登录', iconComponent: SwitchButton },
]

export type UserCenterNavConfig = typeof userCenterNavConfig
